import type { Country } from '@seek/melways-sites';
import { OFFICE_ADDRESS, OFFICE_ADDRESS_THAI } from '@seek/shared-constants';
import { useTranslations } from '@vocab/react';
import {
  Heading,
  Box,
  Stack,
  Strong,
  Dropdown,
  Divider,
} from 'braid-design-system';
import React, { useState } from 'react';

import {
  COUNTRY_NAME_LOOKUP,
  COUNTRY_LOOKUP,
  SITENAME_LOOKUP,
} from 'src/shared/constant';
import { useConfig } from 'src/shared/hooks';
import type { CandidateSite, Site, SitesAll } from 'src/shared/types';

import translations from '../../.vocab';

import { AdditionalInfo } from './AdditionalInfo';
import CustomerServiceHours from './CustomerServiceHours';
import { OfficeInfo } from './OfficeInfo';
import { SocialMediaInfo } from './SocialMediaInfo';

type CountryInfo = Record<Country, Record<string, string>>;

const getAddress = (languageName: string) =>
  languageName === 'th-TH' ? OFFICE_ADDRESS_THAI : OFFICE_ADDRESS;

export const ContactInfo = () => {
  const { site, language, country } = useConfig();
  const [selectedCountry, setSelectedCountry] = useState<Country>(country);
  const [selectedSite, setSelectedSite] = useState<SitesAll>(site);
  const { t } = useTranslations(translations);

  /*
  / Filter the `OFFICE_ADDRESS` object to just 'candidate' locations
  / Return the list sorted by country
  */

  const office_address = React.useMemo(() => {
    const USE_OFFICE_ADDRESS = getAddress(language);

    const addressList = Object.entries(USE_OFFICE_ADDRESS)
      .filter(([k, _v]) => k.includes(site) || k.includes('candidate'))
      .reduce(
        (a, [k, { ...locations }]) => ({
          ...a,
          [COUNTRY_LOOKUP[k as CandidateSite]]: locations,
        }),
        {} as CountryInfo,
      );

    return addressList;
  }, [language, site]);

  const handleDropdownChange = (e: React.FormEvent<HTMLSelectElement>) => {
    const value = e.currentTarget.value as Country;
    setSelectedCountry(value);
    setSelectedSite(SITENAME_LOOKUP[value] as Site);
  };

  return (
    <Box paddingX={{ mobile: 'none', desktop: 'gutter' }} paddingRight="none">
      <Stack space="large">
        <Heading level="4">
          <Strong>{t('Contact Information')}</Strong>
        </Heading>
        <Box>
          <Dropdown
            id="countries"
            label="Location"
            onChange={(e) => handleDropdownChange(e)}
            value={selectedCountry}
          >
            {Object.entries(office_address).map(([k]) => (
              <option key={k} value={k}>
                {COUNTRY_NAME_LOOKUP[k as Country]}
              </option>
            ))}
          </Dropdown>
        </Box>

        <Stack space="medium">
          <CustomerServiceHours site={selectedSite} />
          {(selectedCountry === 'au' || selectedCountry === 'nz') && (
            <Stack space="medium">
              <AdditionalInfo site={selectedSite} />
              <SocialMediaInfo site={selectedSite} />
            </Stack>
          )}
        </Stack>
        <Divider />

        <Stack space="large">
          {Object.entries(office_address[selectedCountry]).map(
            ([k, location]: Array<string | any>) => {
              if (k === 'country_name' || k === 'MobileHotline') {
                return;
              }
              if (!location.address) {
                return;
              }
              return <OfficeInfo key={k} location={location} />;
            },
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
